import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

import { unflatten } from '../../../../shared/i18n/unflatten';
import { TenantConfig } from './tenant-config.service';

/**
 * Story 25.5 (Phase D) — applies operator login-text overrides from a fetched
 * TenantConfig onto Transloco, live. For each language in `i18n_overrides`, the
 * flat `key -> text` map is unflattened to a nested translation object and merged
 * into that language's translations (`{ merge: true }`), so operator overrides
 * win over the bundled defaults while leaving every non-overridden key intact.
 *
 * Transloco loads each language's base JSON LAZILY (the first time it becomes
 * active). A merge applied before a language's base is loaded — or a base that
 * (re)loads after a merge — would otherwise replace the override, so the page
 * shows the built-in defaults after a language switch until a full reload
 * (`reRenderOnLangChange` only re-renders; it does NOT preserve overrides across
 * a lazy base load). To stay correct, we re-merge the stored overrides every time
 * Transloco finishes loading a language (`translationLoadSuccess`). emjo-login
 * only — brand-site has no per-tenant overrides.
 */
@Injectable({ providedIn: 'root' })
export class TenantI18nService {
  private readonly transloco = inject(TranslocoService);
  private overrides: Record<string, Record<string, string>> = {};
  private subscribed = false;

  apply(config: TenantConfig | null | undefined): void {
    const overrides = config?.i18n_overrides;
    if (!overrides) {
      return;
    }
    this.overrides = overrides;
    this.reapplyOnLanguageLoad();
    // Merge now for any language already loaded (e.g. the active one); languages
    // loaded later (on switch) are handled by the subscription above.
    for (const lang of Object.keys(overrides)) {
      this.mergeFor(lang);
    }
  }

  private reapplyOnLanguageLoad(): void {
    if (this.subscribed) {
      return;
    }
    this.subscribed = true;
    this.transloco.events$
      .pipe(filter((e) => e.type === 'translationLoadSuccess'))
      .subscribe((e) => this.mergeFor((e as { payload: { langName: string } }).payload.langName));
  }

  private mergeFor(lang: string): void {
    const flat = this.overrides[lang];
    if (!flat || Object.keys(flat).length === 0) {
      return;
    }
    this.transloco.setTranslation(unflatten(flat), lang, { merge: true });
  }
}
